import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Award, GraduationCap, FileText, TrendingUp, Sparkles,
  AlertTriangle, CheckCircle2, RefreshCw, Building2, Calendar,
  Activity, Target, Lightbulb, Brain, ShieldAlert, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";
import EmployeeDossierPDF from "@/components/EmployeeDossierPDF";

const fmtBRL = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};
const fmtMonths = (m) => {
  if (m == null) return "—";
  if (m < 12) return `${m} mês${m === 1 ? "" : "es"}`;
  const y = Math.floor(m / 12); const r = m % 12;
  return r === 0 ? `${y} ${y === 1 ? "ano" : "anos"}` : `${y}a ${r}m`;
};

export default function PerformanceIndividualDetailPage() {
  const { employeeId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showDossier, setShowDossier] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    api.get(`/performance/individual/${employeeId}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));

    setRecLoading(true);
    api.get(`/performance/individual/${employeeId}/recommended-trainings`)
      .then(({ data }) => setRecommended(data))
      .catch(() => {})
      .finally(() => setRecLoading(false));
  }, [employeeId]);

  const generateAi = async (force = false) => {
    setAiLoading(true);
    try {
      const { data } = await api.post(
        `/performance/individual/${employeeId}/ai-analysis${force ? "?force=true" : ""}`,
      );
      setAiAnalysis(data);
      toast.success(data.from_cache ? "Análise carregada (cache)." : "Análise gerada com IA.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao gerar análise.");
    } finally { setAiLoading(false); }
  };

  if (loading || !data) {
    return (
      <div className="space-y-3 pb-10">
        <Skeleton className="h-[180px]" />
        <Skeleton className="h-[80px]" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  const { employee, company, sector, consolidated, appraisals, competency_gaps,
          trainings, training_kpis, promotions, absences } = data;

  const scoreColor = consolidated.media_avaliacoes == null ? "slate"
    : consolidated.media_avaliacoes >= 4 ? "emerald"
    : consolidated.media_avaliacoes >= 3 ? "ochre" : "rose";

  return (
    <div className="space-y-5 pb-12" data-testid="perf-individual-detail-page">
      <Link to="/performance/individuos"
            className="inline-flex items-center text-[12px] text-muted-foreground hover:text-[#234935] transition-colors"
            data-testid="pid-back">
        <ArrowLeft className="h-3 w-3 mr-1" /> Voltar para Relatórios Individuais
      </Link>

      <PageHero
        kicker={<><Activity className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Dossiê de Performance</>}
        title={employee.nome}
        description={`${employee.cargo || "—"} · Matrícula ${employee.matricula} · ${sector?.name || "Sem setor"} · ${company?.nome_fantasia || ""}`}
        actions={
          <Button size="sm" onClick={() => setShowDossier(true)}
                  className="h-10 bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="pid-dossier-btn">
            <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Gerar Dossiê PDF
          </Button>
        }
      />

      <EmployeeDossierPDF employeeId={employeeId}
                          open={showDossier}
                          onClose={() => setShowDossier(false)} />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pid-kpis">
        <KpiCard label="Score consolidado" value={consolidated.media_avaliacoes ?? "—"}
                 sub={consolidated.media_setor != null ? `Setor: ${consolidated.media_setor}` : "Sem benchmark"}
                 icon={Award} accent={scoreColor} testId="pid-score" />
        <KpiCard label="Treinamentos concluídos" value={training_kpis.concluidos}
                 sub={`${training_kpis.horas_concluidas}h · ${training_kpis.taxa_conclusao_pct}% taxa`}
                 icon={GraduationCap} accent="ochre" testId="pid-trainings" />
        <KpiCard label="Promoções" value={consolidated.total_promocoes}
                 sub={consolidated.ultima_promocao_em ? `Última em ${fmtDate(consolidated.ultima_promocao_em)}` : "Sem histórico"}
                 icon={TrendingUp} accent="emerald" testId="pid-promotions" />
        <KpiCard label="Tempo de empresa" value={fmtMonths(consolidated.tempo_empresa_meses)}
                 sub={`No cargo: ${fmtMonths(consolidated.tempo_cargo_meses)}`}
                 icon={Calendar} accent="sky" testId="pid-tenure" />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-100" data-testid="pid-tabs">
          <TabsTrigger value="overview" data-testid="pid-tab-overview">Visão geral</TabsTrigger>
          <TabsTrigger value="appraisals" data-testid="pid-tab-appraisals">Avaliações ({appraisals.length})</TabsTrigger>
          <TabsTrigger value="trainings" data-testid="pid-tab-trainings">Treinamentos ({trainings.length})</TabsTrigger>
          <TabsTrigger value="recommended" data-testid="pid-tab-recommended">Recomendados</TabsTrigger>
          <TabsTrigger value="promotions" data-testid="pid-tab-promotions">Promoções ({promotions.length})</TabsTrigger>
          <TabsTrigger value="ai" data-testid="pid-tab-ai" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#234935] data-[state=active]:to-[#2c5c43] data-[state=active]:text-white">
            <Sparkles className="h-3 w-3 mr-1" /> IA
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section kicker="Cadastral" title="Identificação">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
                <dt className="text-muted-foreground">Nome</dt><dd>{employee.nome}</dd>
                <dt className="text-muted-foreground">Matrícula</dt><dd className="font-mono">{employee.matricula || "—"}</dd>
                <dt className="text-muted-foreground">Cargo</dt><dd>{employee.cargo || "—"}</dd>
                <dt className="text-muted-foreground">Salário</dt><dd className="font-mono tabular-nums">{fmtBRL(employee.salario)}</dd>
                <dt className="text-muted-foreground">Setor</dt><dd>{sector?.name || "—"}</dd>
                <dt className="text-muted-foreground">Empresa</dt><dd>{company?.nome_fantasia || "—"}</dd>
                <dt className="text-muted-foreground">Admissão</dt><dd>{fmtDate(employee.data_admissao)}</dd>
                <dt className="text-muted-foreground">Status</dt><dd className="capitalize">{employee.status}</dd>
              </dl>
            </Section>

            <Section kicker="Gaps" title="Competências a desenvolver" description="As 5 mais baixas da última avaliação">
              {competency_gaps.length === 0 ? (
                <div className="text-[12px] text-muted-foreground">Sem avaliações com pontuação.</div>
              ) : (
                <div className="space-y-2">
                  {competency_gaps.map((g) => (
                    <div key={g.competency} className="flex items-center justify-between gap-2"
                         data-testid={`pid-gap-${g.competency}`}>
                      <span className="text-[12.5px] capitalize">{g.competency.replace(/_/g, " ")}</span>
                      <div className="flex items-center gap-2 flex-1 max-w-[200px]">
                        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full ${g.score < 3 ? "bg-rose-500" : g.score < 4 ? "bg-[#C5A059]" : "bg-emerald-500"}`}
                               style={{ width: `${(g.score / 5) * 100}%` }} />
                        </div>
                        <span className="text-[11px] font-mono tabular-nums w-8 text-right">{g.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>

          <Section kicker="Absenteísmo" title="Histórico (últimos 12 meses)">
            {absences.data_status === "vazio" ? (
              <div className="text-[12px] text-muted-foreground italic">Sem dados de absenteísmo no sistema.</div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Horas perdidas</div>
                  <div className="text-[24px] font-display font-semibold tabular-nums">{absences.horas_perdidas_12m}h</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ocorrências</div>
                  <div className="text-[24px] font-display font-semibold tabular-nums">{absences.ocorrencias_12m}</div>
                </div>
              </div>
            )}
          </Section>
        </TabsContent>

        {/* APPRAISALS */}
        <TabsContent value="appraisals" className="mt-4">
          <Section kicker="Histórico" title="Ciclos de avaliação">
            {appraisals.length === 0 ? (
              <div className="text-center py-10 text-[12.5px] text-muted-foreground">
                Sem avaliações registradas.
              </div>
            ) : (
              <div className="space-y-2">
                {appraisals.map((a) => (
                  <div key={a.id} className="rounded-lg border border-border bg-white p-3" data-testid={`pid-appr-${a.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="font-medium text-[13.5px]">{a.cycle_name}</div>
                        <div className="text-[10.5px] text-muted-foreground">{fmtDate(a.completed_at) || "Em andamento"}</div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className={
                          a.status === "concluido" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          "bg-amber-50 text-amber-800 border-amber-200"
                        }>{a.status}</Badge>
                        {a.final_score != null && (
                          <div className="text-[18px] font-display font-bold tabular-nums text-[#234935] mt-1">
                            {a.final_score}<span className="text-[10px] text-muted-foreground">/5</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>Auto: <b className="font-mono tabular-nums">{a.self_score ?? "—"}</b></div>
                      <div>Gestor: <b className="font-mono tabular-nums">{a.manager_score ?? "—"}</b></div>
                    </div>
                    {(a.manager_comment || a.self_comment) && (
                      <div className="mt-2 pt-2 border-t border-slate-100 text-[11.5px] text-slate-700 italic">
                        {a.manager_comment && <p className="mb-1">📋 <b>Gestor:</b> {a.manager_comment}</p>}
                        {a.self_comment && <p>💭 <b>Auto:</b> {a.self_comment}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* TRAININGS */}
        <TabsContent value="trainings" className="mt-4">
          <Section kicker="Histórico" title="Treinamentos realizados">
            {trainings.length === 0 ? (
              <div className="text-center py-10 text-[12.5px] text-muted-foreground">
                Sem treinamentos registrados.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {trainings.map((t) => (
                  <div key={t.enrollment_id} className="rounded-lg border border-border bg-white p-3"
                       data-testid={`pid-train-${t.enrollment_id}`}>
                    <div className="flex items-start gap-2">
                      <GraduationCap className="h-4 w-4 text-[#C5A059] mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[12.5px] truncate">{t.name}</div>
                        <div className="text-[10.5px] text-muted-foreground">
                          {t.tipo} · {t.carga_horaria}h · {t.modalidade}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          {t.concluido
                            ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Concluído
                              </Badge>
                            : <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-200">Em andamento</Badge>}
                          {t.obrigatorio && <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">Obrigatório</Badge>}
                          {t.nota != null && <span className="text-[10.5px] font-mono tabular-nums">Nota: {t.nota}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* RECOMMENDED TRAININGS */}
        <TabsContent value="recommended" className="mt-4">
          <Section
            kicker="Sugestões"
            title="Treinamentos recomendados"
            description="Baseado nos gaps de competência da última avaliação, treinamentos obrigatórios pendentes e cargo do colaborador."
          >
            {recLoading ? (
              <Skeleton className="h-32" />
            ) : !recommended || recommended.items.length === 0 ? (
              <div className="text-center py-8 text-[12.5px] text-muted-foreground" data-testid="pid-rec-empty">
                <Lightbulb className="h-8 w-8 mx-auto text-[#C5A059] mb-2" />
                Nenhum treinamento ativo elegível encontrado.
                {recommended?.competency_gaps?.length > 0 && (
                  <div className="mt-2 text-[11px]">
                    Gaps identificados: {recommended.competency_gaps.join(", ")}.
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {recommended.items.map((r) => (
                  <div key={r.training_id}
                       className="rounded-lg border-2 border-[#C5A059]/40 bg-gradient-to-br from-[#FAF4E5] to-white p-3"
                       data-testid={`pid-rec-${r.training_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Target className="h-3.5 w-3.5 text-[#8A6B2B] shrink-0" />
                        <div className="font-medium text-[13px] truncate">{r.name}</div>
                      </div>
                      <Badge variant="outline" className="text-[9px] bg-[#234935] text-white border-[#234935]">
                        Score {r.score}
                      </Badge>
                    </div>
                    <div className="text-[10.5px] text-muted-foreground mb-1.5">
                      {r.tipo} · {r.carga_horaria}h · {r.modalidade}
                      {r.obrigatorio && <Badge variant="outline" className="ml-1 text-[9px] bg-rose-50 text-rose-700 border-rose-200">Obrigatório</Badge>}
                    </div>
                    <ul className="text-[11px] text-slate-700 space-y-0.5">
                      {r.reasons.map((reason, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-[#C5A059] mt-0.5">›</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* PROMOTIONS */}
        <TabsContent value="promotions" className="mt-4">
          <Section kicker="Carreira" title="Histórico de promoções">
            {promotions.length === 0 ? (
              <div className="text-center py-10 text-[12.5px] text-muted-foreground">
                Sem promoções registradas.
              </div>
            ) : (
              <div className="space-y-2">
                {promotions.map((p) => (
                  <div key={p.id} className="rounded-lg border border-border bg-white p-3" data-testid={`pid-promo-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10.5px] text-muted-foreground">{fmtDate(p.issued_at)}</div>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">De</div>
                            <div className="text-[12.5px] truncate">{p.cargo_anterior || "—"}</div>
                            <div className="text-[10.5px] font-mono tabular-nums text-muted-foreground">{fmtBRL(p.salario_anterior)}</div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-[#8A6B2B]">Para</div>
                            <div className="text-[12.5px] font-medium truncate">{p.new_cargo}</div>
                            <div className="text-[10.5px] font-mono tabular-nums font-semibold text-[#234935]">{fmtBRL(p.new_salario)}</div>
                          </div>
                        </div>
                      </div>
                      {p.diff_pct != null && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          +{p.diff_pct}%
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai" className="mt-4">
          <Section
            kicker="Inteligência"
            title="Análise com IA"
            description="Diagnóstico executivo gerado por Claude Sonnet 4.5 considerando todo o histórico de performance, treinamentos e promoções do colaborador."
            action={
              <div className="flex items-center gap-2">
                {aiAnalysis && (
                  <Button size="sm" variant="outline" onClick={() => generateAi(true)} disabled={aiLoading}
                          data-testid="pid-ai-regenerate">
                    <RefreshCw className={`h-3 w-3 mr-1 ${aiLoading ? "animate-spin" : ""}`} /> Regenerar
                  </Button>
                )}
                {!aiAnalysis && (
                  <Button size="sm" onClick={() => generateAi(false)} disabled={aiLoading}
                          className="bg-gradient-to-r from-[#234935] to-[#2c5c43] text-white"
                          data-testid="pid-ai-generate">
                    <Sparkles className={`h-3 w-3 mr-1 ${aiLoading ? "animate-pulse" : ""}`} />
                    {aiLoading ? "Analisando..." : "Gerar análise"}
                  </Button>
                )}
              </div>
            }
          >
            {!aiAnalysis ? (
              <div className="text-center py-12 px-4 rounded-lg border-2 border-dashed border-[#C5A059]/30 bg-gradient-to-br from-[#FAF4E5]/30 to-white"
                   data-testid="pid-ai-empty">
                <Brain className="h-12 w-12 mx-auto text-[#C5A059] mb-3" />
                <div className="font-display text-[16px] font-semibold mb-1">Análise sob demanda</div>
                <p className="text-[12.5px] text-muted-foreground max-w-md mx-auto">
                  Clique em <b>Gerar análise</b> para que a IA leia todo o dossiê de {employee.nome.split(" ")[0]} e
                  produza um diagnóstico com pontos fortes, atenção, recomendações e risco de retenção.
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="pid-ai-result">
                <div className="rounded-lg bg-gradient-to-br from-[#234935] to-[#1A231F] text-white p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-[#C5A059]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059] font-semibold">Resumo executivo</span>
                  </div>
                  <p className="text-[14px] leading-relaxed" data-testid="pid-ai-resumo">{aiAnalysis.analysis.resumo_executivo}</p>
                  {aiAnalysis.from_cache && (
                    <div className="mt-2 text-[10px] text-white/60 italic">
                      Gerado em {fmtDate(aiAnalysis.generated_at)} (cache válido por 7 dias)
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="pid-ai-fortes">
                    <div className="flex items-center gap-1.5 mb-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      <span className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Pontos fortes</span>
                    </div>
                    <ul className="space-y-1.5 text-[12.5px] text-slate-800">
                      {(aiAnalysis.analysis.pontos_fortes || []).map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-emerald-700 mt-0.5">›</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4" data-testid="pid-ai-atencao">
                    <div className="flex items-center gap-1.5 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-700" />
                      <span className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Pontos de atenção</span>
                    </div>
                    <ul className="space-y-1.5 text-[12.5px] text-slate-800">
                      {(aiAnalysis.analysis.pontos_atencao || []).map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-amber-700 mt-0.5">›</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="rounded-lg border border-[#C5A059]/30 bg-gradient-to-br from-[#FAF4E5]/40 to-white p-4" data-testid="pid-ai-recs">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Lightbulb className="h-4 w-4 text-[#8A6B2B]" />
                    <span className="text-[11px] uppercase tracking-wider text-[#8A6B2B] font-semibold">Recomendações de desenvolvimento</span>
                  </div>
                  <ul className="space-y-1.5 text-[12.5px] text-slate-800">
                    {(aiAnalysis.analysis.recomendacoes || []).map((r, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-[#C5A059] mt-0.5 font-bold">{i + 1}.</span><span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <RiskCard risco={aiAnalysis.analysis.risco_retencao}
                          justificativa={aiAnalysis.analysis.justificativa_risco} />
              </div>
            )}
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RiskCard({ risco, justificativa }) {
  const cfg = {
    baixo: { color: "emerald", label: "Baixo risco de retenção", icon: CheckCircle2 },
    medio: { color: "amber", label: "Risco médio de retenção", icon: AlertTriangle },
    alto: { color: "rose", label: "Risco alto de retenção", icon: ShieldAlert },
  }[risco] || { color: "slate", label: "Sem classificação", icon: ShieldAlert };
  const Icon = cfg.icon;
  return (
    <div className={`rounded-lg border p-4 ${
      cfg.color === "rose" ? "border-rose-200 bg-rose-50/50" :
      cfg.color === "amber" ? "border-amber-200 bg-amber-50/50" :
      cfg.color === "emerald" ? "border-emerald-200 bg-emerald-50/50" :
      "border-slate-200 bg-slate-50"
    }`} data-testid="pid-ai-risk">
      <div className="flex items-start gap-2">
        <Icon className={`h-5 w-5 shrink-0 ${
          cfg.color === "rose" ? "text-rose-700" :
          cfg.color === "amber" ? "text-amber-700" :
          cfg.color === "emerald" ? "text-emerald-700" : "text-slate-500"
        }`} />
        <div>
          <div className="font-display font-semibold text-[13.5px]">{cfg.label}</div>
          <p className="text-[12px] text-slate-700 mt-0.5">{justificativa}</p>
        </div>
      </div>
    </div>
  );
}
